import {ProminentBannerSection_CouponSlider} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/article_detail_page/v1/service.types';
import {doAsync} from '@aboutyou/utils';
import React, {useEffect} from 'react';
import {useActivatePreselectedBestCouponABTest} from '../../../../features/Coupons/useActivatePreselectedBestCouponABTest';
import {useActivatedCouponState} from '../../../../features/Coupons/useActivatedCouponState';
import {useEntryUrlCrmCouponTokenParam} from '../../../../features/Coupons/useCrmCouponTokenParam';
import {useActivateCouponProductByCouponPromotionId} from '../../../../features/ProductMiniAdp/utils/useActivateCouponProductByCouponPromotionId';
import {SelectedSize} from '../../../../features/SizeSelection/hooks/useSelectedSizeState';
import {useIsQuickView} from '../../QuickView/useIsQuickView';
import {CouponVariant} from '../ProminentBannerSectionView';
import {CouponSlider} from './CouponSlider/CouponSlider';
import {CouponStickyBar} from './CouponStickyBar/CouponStickyBar';
import {useActivateCouponFromUrl} from './utils/useActivateCouponFromUrl';

interface CouponSectionProps {
  className?: string;
  style?: React.CSSProperties;
  couponSlider: ProminentBannerSection_CouponSlider;
  couponVariant: CouponVariant;
  flatBorders?: boolean;
  productId: number;
  selectedSize?: SelectedSize;
  isInMiniADPDialog: boolean;
  removeNegativeMarginTop?: boolean;
  selectedCouponPromotionId?: string;
}

export const CouponSection: React.FC<CouponSectionProps> = ({
  className,
  couponSlider,
  couponVariant,
  flatBorders,
  productId,
  selectedSize,
  isInMiniADPDialog,
  removeNegativeMarginTop,
  selectedCouponPromotionId,
}) => {
  const isQuickView = useIsQuickView();

  useActivateCouponFromUrl({
    productId,
    coupons: couponSlider.coupons,
  });

  // The ab_preselect_best_coupon A/B test preselects the best product coupon on the stream pages.
  // Therefore, we activate it if it's present.
  useActivateCouponProductByCouponPromotionId({
    productId,
    couponPromotionId: selectedCouponPromotionId,
    coupons: couponSlider.coupons,
    disabled: isQuickView,
  });

  useActivatePreselectedBestCouponABTest({
    disabled: isQuickView,
  });

  const {setHeadline, activatedCoupon} = useActivatedCouponState(productId);
  const isCrmCouponEntry = useEntryUrlCrmCouponTokenParam();

  useEffect(() => {
    // if the user is entering the page with CRM `cpn`, then the header is set by the `GetUnlockCouponInfo` -> check `useUnlockCouponInfo.ts`
    // but in some edge cases, the unlocked coupon is not applied to the product (ex: App Only Coupon) -> in this case, we need to fallback to the default headline (Save Up To %)
    if (isCrmCouponEntry && activatedCoupon) return;
    // eslint-disable-next-line local-rules/set-state-in-use-effect
    doAsync(() => setHeadline(couponSlider.headline));
  }, [couponSlider.headline, isCrmCouponEntry, setHeadline, activatedCoupon]);

  switch (couponVariant) {
    case 'coupon-slider':
      return (
        <CouponSlider
          className={className}
          couponSlider={couponSlider}
          productId={productId}
          flatBorders={flatBorders}
          removeNegativeMarginTop={removeNegativeMarginTop}
        />
      );
    case 'coupon-sticky-bar':
      return (
        <CouponStickyBar
          className={className}
          couponSlider={couponSlider}
          productId={productId}
          selectedSize={selectedSize}
          isInMiniADPDialog={isInMiniADPDialog}
        />
      );
  }

  return null;
};
